// frontend/src/services/followUp.js

/**
 * Service client for the "follow-up reminder" feature.
 *
 * Backed by the endpoints shipped in fur001 (Phase 1) +
 * send-due-follow-up-reminders sweeper (Phase 2). See
 * `backend/docs/follow-up-reminders.md` for the full architecture.
 *
 * All methods are thin axios wrappers; the shared ``api`` instance
 * handles base URL, Bearer-token injection, and 401 refresh.
 */

import api from './api';

const followUpService = {
  /**
   * Get the current user's follow-up popup preferences.
   *
   * Lazy-creates the row on the server side if it doesn't exist yet —
   * a fresh user gets defaults (popup_dismissed=false, no saved slot).
   *
   * @returns {Promise<{popup_dismissed: boolean, default_days_offset: number|null, default_hour: number|null}>}
   */
  async getPreferences() {
    const response = await api.get('/users/me/follow-up-preferences');
    return response.data;
  },

  /**
   * Partial-update the popup preferences.
   *
   * @param {Object} payload
   * @param {boolean} [payload.popup_dismissed]
   * @param {number} [payload.default_days_offset] - 1..7
   * @param {number} [payload.default_hour] - 0..23
   * @param {number} [payload.default_minute] - 0, 15, 30 or 45
   * @param {boolean} [payload.clear_default_slot] - When true, nulls
   *   both default_days_offset and default_hour (cannot be combined
   *   with setting them).
   * @returns {Promise<Object>} Updated preferences (full shape).
   */
  async updatePreferences(payload) {
    const response = await api.put('/users/me/follow-up-preferences', payload);
    return response.data;
  },

  /**
   * Reset to factory defaults (popup re-enabled, no saved slot).
   *
   * Intended for a "Reset follow-up reminder choice" button in the
   * settings page (Phase 4).
   *
   * @returns {Promise<Object>} Reset preferences.
   */
  async resetPreferences() {
    const response = await api.post('/users/me/follow-up-preferences/reset');
    return response.data;
  },

  /**
   * Schedule a follow-up reminder for a lead.
   *
   * Atomic on the server: re-scheduling the same lead cancels the
   * prior pending reminder. The two side-effect flags piggyback so
   * the popup is one network round-trip.
   *
   * The payload carries EXACTLY ONE time shape:
   *   - ``{days_offset: 1..7, hour: 0..23, minute: 0|15|30|45}`` - that
   *     calendar day counted from the server's 04:00 logical day; or
   *   - ``{scheduled_for: "<aware ISO-8601>"}`` - an absolute instant.
   * A time outside [now + 5 min, now + 7 days] is a 422 whose
   * ``detail.code`` is REMINDER_TIME_TOO_SOON / REMINDER_TIME_TOO_FAR
   * (see ``reminderTimeErrorCode`` below).
   *
   * @param {string} leadId
   * @param {Object} payload
   * @param {number} [payload.days_offset] - 1..7
   * @param {number} [payload.hour] - 0..23 (Asia/Jerusalem)
   * @param {number} [payload.minute] - 0, 15, 30 or 45
   * @param {string} [payload.scheduled_for] - aware ISO instant
   * @param {boolean} [payload.remember_choice=false] - Save the day
   *   offset + time as the default (day-offset shape only).
   * @param {boolean} [payload.dismiss_popup=false] - Suppress the
   *   popup for all future 'contacted' clicks.
   * @returns {Promise<Object>} The freshly created reminder row.
   */
  async createReminder(leadId, payload) {
    const response = await api.post(`/leads/${leadId}/follow-up-reminder`, payload);
    return response.data;
  },

  /**
   * Cancel the active follow-up reminder for a lead, if one exists.
   *
   * Idempotent: returns ``{cancelled: false, reminder: null}`` when
   * nothing was pending (so the UI can call this on close-without-
   * action without first checking state).
   *
   * @param {string} leadId
   * @returns {Promise<{cancelled: boolean, reminder: Object|null}>}
   */
  async cancelReminder(leadId) {
    const response = await api.delete(`/leads/${leadId}/follow-up-reminder`);
    return response.data;
  },

  /**
   * Move the lead's pending reminder to a new time (the same time shapes
   * as ``createReminder``, no side-effect flags). 404 when nothing is
   * pending any more.
   *
   * @param {string} leadId
   * @param {Object} payload
   * @returns {Promise<Object>} The new pending reminder row.
   */
  async rescheduleReminder(leadId, payload) {
    const response = await api.patch(`/leads/${leadId}/follow-up-reminder`, payload);
    return response.data;
  },

  /**
   * The lead's pending reminder, or null when there is none.
   *
   * @param {string} leadId
   * @returns {Promise<Object|null>}
   */
  async getPendingReminder(leadId) {
    const response = await api.get(`/leads/${leadId}/follow-up-reminder`);
    return response.data;
  },

  /**
   * Every pending reminder of the current user, soonest first. ONE call
   * feeds every reminder chip on the page (hooks/usePendingReminders.js).
   *
   * @returns {Promise<Array<{lead_id: string, scheduled_for: string}>>}
   */
  async listPendingReminders() {
    const response = await api.get('/follow-up-reminders/pending');
    return response.data;
  },
};

/**
 * The window code of a 422 from POST / PATCH, or null for any other error.
 * The UI maps the code to its own approved copy and never shows the
 * server's English ``message``.
 *
 * @param {unknown} err an axios error
 * @returns {"REMINDER_TIME_TOO_SOON"|"REMINDER_TIME_TOO_FAR"|null}
 */
export function reminderTimeErrorCode(err) {
  const response = err && err.response;
  if (!response || response.status !== 422) return null;
  const detail = response.data && response.data.detail;
  const code = detail && typeof detail === 'object' ? detail.code : null;
  return code === 'REMINDER_TIME_TOO_SOON' || code === 'REMINDER_TIME_TOO_FAR'
    ? code
    : null;
}

export default followUpService;
